/**
 * ConfidenceScale Component
 *
 * Reusable confidence scale used across multiple timepoints.
 * Measures topic-specific confidence at a given moment in time.
 *
 * Core Items (always shown):
 * 1. conf_understand - Understanding confidence (also captures mental clarity;
 *    the former conf_clarity item was merged into this one)
 * 2. conf_answer - Certainty about answering questions
 *
 * Optional Item (when includePrediction=true):
 * 3. conf_score - Predicted quiz performance (metacognitive calibration)
 *
 * Used at:
 * - R1: Baseline state confidence (both roles)
 * - R3/R4: Pre-communication confidence (Student only)
 * - R6: Post-chat confidence (both roles)
 * - R7: Post-quiz confidence (both roles)
 */

import React from "react";

// Core confidence questions (2-item scale)
export const CONFIDENCE_QUESTIONS = [
  {
    key: "conf_understand",
    label: "How confident are you in your understanding of this topic?",
    lowLabel: "Not at all confident",
    highLabel: "Very confident"
  },
  {
    key: "conf_answer",
    label: "How certain are you that you could answer questions about this topic?",
    lowLabel: "Not at all certain",
    highLabel: "Very certain"
  }
];

// Prediction question (optional, for metacognitive calibration).
// Scale matches the quiz half size (6 for a 12-question quiz, 9 for 18, etc.).
// Label is dynamically set based on condition via getPredictionLabel().
export const PREDICTION_QUESTION = {
  key: "predictedOwnScore",
  label: "Of the questions, how many do you think you will solve correctly?", // Default, overridden by predictionLabel prop
  scale: 6 // overridden per quiz-half count where the component knows it
};

/**
 * Get the prediction question label based on role and condition.
 * @param {string} condition - Learning condition (ai, pdf, ai-fixed, ai-pdf, web)
 * @param {string} role - Role (teacher, student, learner)
 * @param {string} type - Prediction type (self, partner, postQuiz)
 * @param {number} count - Number of questions in this quiz half (e.g. 6 or 9)
 */
export function getPredictionLabel(condition, role, type = "self", count = 6) {
  const conditionLabels = {
    "ai": "the AI",
    "ai-fixed": "the AI tutor",
    "ai-pdf": "the AI explanation",
    "pdf": "the PDF materials",
    "web": "web search"
  };

  // Student always references "your partner", others reference their learning source
  const source = role === "student"
    ? "your partner"
    : (conditionLabels[condition] || "the learning materials");

  if (type === "postQuiz") {
    // Post-quiz: past tense (postdiction over the questions just answered)
    return `Of the ${count} questions you just answered, how many do you think you got right?`;
  }

  if (type === "partner") {
    // Teacher predicting partner's score - emphasize it's about the PARTNER, not self
    return `Based on what you taught, how many of the ${count} questions do you think YOUR PARTNER will get right?`;
  }

  // Self prediction (forward-looking)
  return `Based on what you learned from ${source}, how many of the ${count} questions do you think you will get right?`;
}

/**
 * ConfidenceScale Component
 * Renders the confidence scale with a title and instructions
 * @param includePrediction - If true, adds the quiz score prediction question
 * @param predictionLabel - Custom label for the prediction question (uses getPredictionLabel)
 * @param questionPrefix - Optional prefix to prepend to each question (e.g., "After the quiz, ")
 */
export function ConfidenceScale({
  title = "Confidence Assessment",
  instructions = "Please rate your current confidence:",
  values = {},
  onChange,
  color = "blue",
  topicName = "the topic",
  compact = false,
  startNumber = 1,
  showHeader = true,
  excludeQuestions = [],
  includePrediction = false,
  predictionLabel = null,
  questionPrefix = "",
  labelOverrides = {}
}) {
  const colorClasses = {
    blue: {
      selected: "bg-blue-600 text-white shadow-md scale-110",
      unselected: "bg-gray-100 text-gray-700 hover:bg-gray-200",
      header: "text-blue-800",
      bg: "bg-blue-50 border-blue-200"
    },
    emerald: {
      selected: "bg-emerald-600 text-white shadow-md scale-110",
      unselected: "bg-gray-100 text-gray-700 hover:bg-gray-200",
      header: "text-emerald-800",
      bg: "bg-emerald-50 border-emerald-200"
    },
    purple: {
      selected: "bg-purple-600 text-white shadow-md scale-110",
      unselected: "bg-gray-100 text-gray-700 hover:bg-gray-200",
      header: "text-purple-800",
      bg: "bg-purple-50 border-purple-200"
    },
    violet: {
      selected: "bg-violet-600 text-white shadow-md scale-110",
      unselected: "bg-gray-100 text-gray-700 hover:bg-gray-200",
      header: "text-violet-800",
      bg: "bg-violet-50 border-violet-200"
    }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  // Build questions list: core questions + optional prediction.
  // labelOverrides lets a caller reword a specific item for its timepoint
  // (e.g. post-quiz, where "could answer questions" reads oddly after answering).
  let questions = CONFIDENCE_QUESTIONS
    .filter(q => !excludeQuestions.includes(q.key))
    .map(q => (labelOverrides[q.key] ? { ...q, label: labelOverrides[q.key] } : q));
  if (includePrediction) {
    // Use custom prediction label if provided
    const predictionQ = predictionLabel
      ? { ...PREDICTION_QUESTION, label: predictionLabel }
      : PREDICTION_QUESTION;
    questions = [...questions, predictionQ];
  }
  const allAnswered = questions.every(q => values[q.key] !== null && values[q.key] !== undefined);

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      {showHeader && !compact && (
        <>
          <h3 className={`text-lg font-semibold ${colors.header}`}>{title}</h3>
          <p className="text-sm text-gray-600">{instructions}</p>
        </>
      )}

      <div className={compact ? "space-y-3" : "space-y-4"}>
        {questions.map((q, index) => {
          // Prediction questions use a 0..scale range (scale = quiz-half size,
          // e.g. 6 or 9); confidence items use the default 0-6. Anything wider
          // than 6 gets the compact button styling and drops the end labels.
          const scaleMax = q.scale || 6;
          const isWideScale = scaleMax > 6;
          const scaleValues = Array.from({ length: scaleMax + 1 }, (_, v) => v);

          // Apply prefix to the label if provided (lowercase first letter when adding prefix)
          const displayLabel = questionPrefix
            ? questionPrefix + q.label.charAt(0).toLowerCase() + q.label.slice(1)
            : q.label;

          return (
            <div key={q.key} className={`${compact ? "p-3" : "p-4"} bg-gray-50 rounded-lg`}>
              <label className={`block ${compact ? "text-sm" : "text-sm"} font-medium text-gray-700 mb-2`}>
                {startNumber + index}. {displayLabel}
              </label>
              <div className="flex items-center gap-2">
                {!isWideScale && (
                  <span className={`${compact ? "text-xs w-20" : "text-xs w-24"} text-gray-500 text-right`}>
                    {q.lowLabel}
                  </span>
                )}
                <div className={`flex ${isWideScale ? "gap-0.5" : "gap-1"} flex-1 justify-center`}>
                  {scaleValues.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => onChange(q.key, v)}
                      className={`${isWideScale ? "w-7 h-8 text-xs" : compact ? "w-8 h-8 text-sm" : "w-10 h-10"} rounded-lg font-medium transition-all ${
                        values[q.key] === v
                          ? colors.selected
                          : colors.unselected
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                {!isWideScale && (
                  <span className={`${compact ? "text-xs w-20" : "text-xs w-24"} text-gray-500`}>
                    {q.highLabel}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Check if all confidence questions are answered
 * @param {Object} values - The confidence values
 * @param {Array} excludeQuestions - Optional array of question keys to exclude
 * @param {boolean} includePrediction - Whether to require the prediction question
 */
export function isConfidenceComplete(values, excludeQuestions = [], includePrediction = false) {
  let questions = CONFIDENCE_QUESTIONS.filter(q => !excludeQuestions.includes(q.key));
  if (includePrediction) {
    questions = [...questions, PREDICTION_QUESTION];
  }
  return questions.every(q => values[q.key] !== null && values[q.key] !== undefined);
}

/**
 * Get empty confidence state object
 * @param {boolean} includePrediction - Whether to include the prediction field
 */
export function getEmptyConfidenceState(includePrediction = false) {
  const state = CONFIDENCE_QUESTIONS.reduce((acc, q) => {
    acc[q.key] = null;
    return acc;
  }, {});
  if (includePrediction) {
    state[PREDICTION_QUESTION.key] = null;
  }
  return state;
}
